import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowRight,
  FileText,
  BookOpen,
  Video,
  Download,
  Clock,
  Search,
} from 'lucide-react';

const categories = ['All', 'Articles', 'Whitepapers', 'Webinars', 'Guides'];

const insights = [
  {
    id: 1,
    type: 'Whitepaper',
    title: 'Finance-Grade MRV: A Complete Checklist',
    description: 'Everything you need to know about building verification-ready sustainability data infrastructure.',
    category: 'Whitepapers',
    readTime: '15 min read',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
    featured: true,
    downloadable: true,
  },
  {
    id: 2,
    type: 'Guide',
    title: 'How to Build Audit-Ready ESG Data Pipelines',
    description: 'Step-by-step guide to engineering sustainability data systems that meet auditor requirements.',
    category: 'Guides',
    readTime: '12 min read',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80',
    featured: true,
    downloadable: true,
  },
  {
    id: 3,
    type: 'Article',
    title: 'The Evolution of Sustainability Reporting: From ESG to Credibility',
    description: 'Understanding the shift from traditional ESG reporting to finance-grade credibility infrastructure.',
    category: 'Articles',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80',
    featured: false,
    downloadable: false,
  },
  {
    id: 4,
    type: 'Webinar',
    title: 'Tokenization of Carbon Credits: Best Practices',
    description: 'Expert panel discussion on implementing blockchain-based environmental asset infrastructure.',
    category: 'Webinars',
    readTime: '45 min watch',
    image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?w=600&q=80',
    featured: false,
    downloadable: false,
  },
  {
    id: 5,
    type: 'Article',
    title: 'ISSB/IFRS S2: What CFOs Need to Know',
    description: 'Breaking down the new sustainability disclosure requirements and their impact on finance teams.',
    category: 'Articles',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?w=600&q=80',
    featured: false,
    downloadable: false,
  },
  {
    id: 6,
    type: 'Guide',
    title: 'Scope 3 Data Collection: A Practical Approach',
    description: 'Strategies for gathering reliable value chain emissions data from suppliers and customers.',
    category: 'Guides',
    readTime: '10 min read',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
    featured: false,
    downloadable: true,
  },
];

const getIcon = (type) => {
  switch (type) {
    case 'Whitepaper':
      return FileText;
    case 'Article':
      return BookOpen;
    case 'Webinar':
      return Video;
    case 'Guide':
      return Download;
    default:
      return FileText;
  }
};

export const Insights = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredInsights = insights.filter((insight) => {
    const matchesSearch =
      insight.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      insight.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || insight.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredInsights = insights.filter((i) => i.featured);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-4">Insights</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
              Resources & Thought Leadership
            </h1>
            <p className="text-xl text-muted-foreground">
              Expert perspectives on sustainability data, reporting, and the future of
              environmental finance.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Resources (Lead Magnets) */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">Featured Resources</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {featuredInsights.map((insight) => {
              const Icon = getIcon(insight.type);
              return (
                <Card
                  key={insight.id}
                  className="overflow-hidden hover-lift border-teal/20 bg-teal/5"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-2/5 aspect-video md:aspect-square relative overflow-hidden">
                      <img
                        src={insight.image}
                        alt={insight.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/60" />
                    </div>
                    <div className="md:w-3/5 p-6 flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" className="bg-teal/10 text-teal">
                          <Icon className="h-3 w-3 mr-1" />
                          {insight.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {insight.readTime}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {insight.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1">
                        {insight.description}
                      </p>
                      <Button variant="teal" className="w-full group">
                        Download Free
                        <Download className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search insights..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList>
                {categories.map((category) => (
                  <TabsTrigger key={category} value={category}>
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </section>

      {/* All Insights */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">All Insights</h2>
          {filteredInsights.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInsights.map((insight) => {
                const Icon = getIcon(insight.type);
                return (
                  <Card key={insight.id} className="overflow-hidden hover-lift border-border/50 bg-card flex flex-col">
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={insight.image}
                        alt={insight.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <Badge variant="secondary" className="bg-card/90 text-foreground">
                          <Icon className="h-3 w-3 mr-1" />
                          {insight.type}
                        </Badge>
                      </div>
                    </div>
                    <CardHeader className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {insight.readTime}
                        </span>
                      </div>
                      <CardTitle className="text-lg">{insight.title}</CardTitle>
                      <CardDescription className="text-sm">
                        {insight.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Button
                        variant={insight.downloadable ? 'teal' : 'outline'}
                        className="w-full group"
                      >
                        {insight.downloadable ? 'Download' : 'Read More'}
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-xl text-muted-foreground mb-4">No insights found.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('All');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary-foreground mb-4">
            Stay Updated on Sustainability Data
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Get the latest insights, frameworks updates, and best practices delivered to your inbox.
          </p>
          <form
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            onSubmit={(e) => {
              e.preventDefault();
              alert('Thank you for subscribing!');
            }}
          >
            <Input
              type="email"
              placeholder="Enter your email"
              className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
              required
            />
            <Button type="submit" variant="secondary" className="text-secondary-foreground">
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Insights;
